import React, { useContext, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./History.scss";
import { TransactionTable } from "./Contant";
import { showUserHistory } from "../../apis/userDetailsApi";
import { ProfileContext } from "../../contexts/APIProvider";

// Define Props for the History component
interface HistoryProps {
  open: boolean;
  handleClose: () => void;
}

// Define transaction type
interface Transaction {
  id: string;
  reason: string;
  cashValue: number;
  createdDate: string;
}

// Define ProfileContext type
interface ProfileContextType {
  history: Transaction[];
  setHistory: React.Dispatch<React.SetStateAction<Transaction[]>>;
  loggedIn: boolean;
}

// Component: Modal Header
const ModalHeader = ({ handleClose }: { handleClose: () => void }) => (
  <div className="modal-header">
    <h4 className="history-modal-title">Transaction History</h4>
    <button
      type="button"
      className="history-close-btn"
      onClick={handleClose}
      aria-label="Close"
    >
      &times;
    </button>
  </div>
);

// Component: Modal Footer
const ModalFooter = ({ handleClose }: { handleClose: () => void }) => (
  <div className="modal-footer">
    <button
      type="button"
      className="history-footer-close"
      onClick={handleClose}
    >
      Close
    </button>
  </div>
);

// Component: Table Header
const TableHeader = () => (
  <thead>
    <tr>
      {TransactionTable.map((item) => (
        <th key={item.id} scope="col">
          {item.Title}
        </th>
      ))}
    </tr>
  </thead>
);

// Component: No History Fallback
const NoHistoryRow = () => (
  <tr>
    <td colSpan={TransactionTable.length} className="history-no-data">
      No transaction found!
    </td>
  </tr>
);

// Helper: Render history rows
const renderHistoryRows = (history: Transaction[]) =>
  history.map(({ id, reason, cashValue, createdDate }) => {
    const date = new Date(createdDate);
    const formattedDate = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });

    const reasonClass =
      reason === "credit" ? "history-badge-credit" : "history-badge-debit";

    return (
      <tr key={id}>
        <td>{id}</td>
        <td className={reasonClass}>{reason}</td>
        <td>{cashValue}</td>
        <td>{formattedDate}</td>
        <td>{formattedTime}</td>
        <td className="history-badge-success">Success</td>
      </tr>
    );
  });

// Component: Modal Body
const ModalBody = ({
  history,
  showMoreData,
}: {
  history: Transaction[];
  showMoreData: () => void;
}) => (
  <div className="modal-body">
    <table className="history-table">
      <TableHeader />
      <tbody>
        {history.length > 0 ? renderHistoryRows(history) : <NoHistoryRow />}
      </tbody>
    </table>

    {history.length > 0 && (
      <button
        type="button"
        className="history-show-more"
        onClick={showMoreData}
      >
        Show More
      </button>
    )}
  </div>
);

// Main Component: History
function History({ open, handleClose }: HistoryProps) {
  const { history, setHistory, loggedIn } = useContext(
    ProfileContext
  ) as ProfileContextType;
  const [start, setStart] = useState<number>(0);

  useEffect(() => {
    if (!loggedIn) return;

    const fetchInitialHistory = async () => {
      try {
        const response = await showUserHistory(0);
        if (response) {
          setStart(0);
          setHistory(response.data);
        }
      } catch (error) {
        console.error("Error fetching initial history:", error);
      }
    };

    fetchInitialHistory();
  }, [loggedIn, setHistory]);

  const showMoreData = async () => {
    const nextStart = start + 5;
    try {
      const response = await showUserHistory(nextStart);
      if (response) {
        setHistory((prev) => [...prev, ...response.data]);
        setStart(nextStart);
      }
    } catch (error) {
      console.error("Error fetching more data:", error);
    }
  };

  return (
    <Modal size="lg" centered show={open} onHide={handleClose} className="history-modal">
      <div>
        <ModalHeader handleClose={handleClose} />
        <ModalBody history={history} showMoreData={showMoreData} />
        <ModalFooter handleClose={handleClose} />
      </div>
    </Modal>
  );
}

export default History;
